"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Popup = exports.usePopup = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var Portal_1 = require("./Portal");
var useMemoizedArray_1 = require("./useMemoizedArray");
var usePopup_1 = require("./usePopup");
Object.defineProperty(exports, "usePopup", { enumerable: true, get: function () { return usePopup_1.usePopup; } });
var utils_1 = require("./utils");
var DEFAULT_DIRECTIONS = ['bottom', 'top', 'left', 'right'];
var PopupInternal = (0, react_1.forwardRef)(function (_a, externalRef) {
    var open = _a.open, children = _a.children, content = _a.content, _b = _a.directions, externalDirections = _b === void 0 ? DEFAULT_DIRECTIONS : _b, _c = _a.align, align = _c === void 0 ? 'start' : _c, _d = _a.offset, offset = _d === void 0 ? 8 : _d, _e = _a.reposition, reposition = _e === void 0 ? true : _e, _f = _a.parentElement, parentElement = _f === void 0 ? window.document.body : _f, _g = _a.boundaryElement, boundaryElement = _g === void 0 ? parentElement : _g, _h = _a.containerClassName, containerClassName = _h === void 0 ? 'helios--popup' : _h, containerStyle = _a.containerStyle, _j = _a.boundaryInset, boundaryInset = _j === void 0 ? 0 : _j, onClickOutside = _a.onClickOutside, _k = _a.useOutsideClickCapture, useOutsideClickCapture = _k === void 0 ? false : _k;
    var directions = (0, useMemoizedArray_1.useMemoizedArray)(externalDirections);
    var defaultRect = (0, react_1.useMemo)(function () { return new DOMRect(0, 0, 0, 0); }, []);
    var prevDirections = (0, react_1.useRef)();
    var prevReposition = (0, react_1.useRef)(reposition);
    var prevIsOpen = (0, react_1.useRef)(false);
    var childRef = (0, react_1.useRef)();
    var _l = (0, react_1.useState)({
        align: align,
        repositionedLeft: 0,
        repositionedTop: 0,
        direction: directions[0],
        offset: offset,
        childRect: defaultRect,
        popupRect: defaultRect,
        parentRect: defaultRect,
        boundaryRect: defaultRect,
        boundaryInset: boundaryInset,
        collisions: defaultRect,
        hasCollisions: false,
    }), popupState = _l[0], setPopupState = _l[1];
    var onPositionPopup = (0, react_1.useCallback)(function (state) { return setPopupState(state); }, []);
    var _m = (0, usePopup_1.usePopup)({
        open: open,
        childRef: childRef,
        containerClassName: containerClassName,
        parentElement: parentElement,
        boundaryElement: boundaryElement,
        directions: directions,
        align: align,
        offset: offset,
        boundaryInset: boundaryInset,
        reposition: reposition,
        onPositionPopup: onPositionPopup,
    }), positionPopup = _m.positionPopup, popupRef = _m.popupRef;
    (0, react_1.useLayoutEffect)(function () {
        var shouldUpdate = true;
        var updatePopup = function () {
            var _a, _b;
            if (open && shouldUpdate) {
                var childRect = (_a = childRef === null || childRef === void 0 ? void 0 : childRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                var popupRect = (_b = popupRef === null || popupRef === void 0 ? void 0 : popupRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                if (childRect != null &&
                    popupRect != null &&
                    (!(0, utils_1.rectsAreEqual)(childRect, new DOMRect(popupState.childRect.left, popupState.childRect.top, popupState.childRect.width, popupState.childRect.height)) ||
                        popupRect.width !== popupState.popupRect.width ||
                        popupRect.height !== popupState.popupRect.height ||
                        popupState.offset !== offset ||
                        popupState.align !== align ||
                        directions !== prevDirections.current)) {
                    positionPopup();
                }
                if (directions !== prevDirections.current) {
                    prevDirections.current = directions;
                }
                if (reposition !== prevReposition.current) {
                    prevReposition.current = reposition;
                }
                if (shouldUpdate) {
                    window.requestAnimationFrame(updatePopup);
                }
            }
            prevIsOpen.current = open;
        };
        window.requestAnimationFrame(updatePopup);
        return function () {
            shouldUpdate = false;
        };
    }, [
        align,
        open,
        offset,
        popupRef,
        popupState.align,
        popupState.childRect.height,
        popupState.childRect.left,
        popupState.childRect.top,
        popupState.childRect.width,
        popupState.offset,
        popupState.popupRect.height,
        popupState.popupRect.width,
        positionPopup,
        directions,
        reposition,
    ]);
    (0, react_1.useEffect)(function () {
        var popupElement = popupRef.current;
        Object.assign(popupElement.style, containerStyle);
        return function () {
            Object.keys(containerStyle !== null && containerStyle !== void 0 ? containerStyle : {}).forEach(function (key) {
                return delete popupElement.style[key];
            });
        };
    }, [containerStyle, open, popupRef]);
    var outsideClickHandler = (0, react_1.useCallback)(function (e) {
        var _a, _b;
        if (open &&
            !((_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) &&
            !((_b = childRef.current) === null || _b === void 0 ? void 0 : _b.contains(e.target))) {
            onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside(e);
        }
    }, [open, onClickOutside, popupRef]);
    var windowResizeHandler = (0, react_1.useCallback)(function () {
        if (childRef.current) {
            window.requestAnimationFrame(function () { return positionPopup(); });
        }
    }, [positionPopup]);
    (0, react_1.useEffect)(function () {
        var body = parentElement.ownerDocument.body;
        body.addEventListener('click', outsideClickHandler, useOutsideClickCapture);
        body.addEventListener('contextmenu', outsideClickHandler, useOutsideClickCapture);
        body.addEventListener('resize', windowResizeHandler);
        return function () {
            body.removeEventListener('click', outsideClickHandler, useOutsideClickCapture);
            body.removeEventListener('contextmenu', outsideClickHandler, useOutsideClickCapture);
            body.removeEventListener('resize', windowResizeHandler);
        };
    }, [outsideClickHandler, windowResizeHandler, parentElement]);
    var refHandler = (0, react_1.useCallback)(function (node) {
        childRef.current = node;
        if (externalRef != null) {
            if (typeof externalRef === 'object') {
                externalRef.current = node;
            }
            else if (typeof externalRef === 'function') {
                externalRef(node);
            }
        }
    }, [externalRef]);
    var renderChild = function () {
        return (0, react_1.cloneElement)(children, {
            ref: refHandler,
        });
    };
    var renderPopup = function () {
        if (!open)
            return null;
        return ((0, jsx_runtime_1.jsx)(Portal_1.Portal, __assign({ element: popupRef.current, container: parentElement }, { children: typeof content === 'function' ? content(popupState) : content })));
    };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [renderChild(), renderPopup()] }));
});
exports.Popup = (0, react_1.forwardRef)(function (props, ref) {
    if (typeof window === 'undefined')
        return props.children;
    return (0, jsx_runtime_1.jsx)(PopupInternal, __assign({}, props, { ref: ref }));
});
